import { getUrlWithVariant, ProductForm } from '@shopify/theme-product-form';
import { formatMoney } from '@shopify/theme-currency';

import '../../styles/components/product-form.scss';

import { addToCartForm } from '../utils/cart';

class ProductFormElement extends HTMLElement {
  constructor() {
    super();

    this.productImagesContainer = this.closest('[data-product-container]').querySelector('desktop-product-image');
    this.productImagesSwiper = this.productImagesContainer.querySelector('[data-images-swiper]');
    this.productImagesSlides = Array.from(this.productImagesSwiper.querySelectorAll('.swiper-slide'));

    this.priceWrapperElement = this.closest("[data-product-info]").querySelector(".price");
    this.priceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-price]"));
    this.comparePriceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-compare-at-price]"));

    this.productHandle = this.getAttribute('product-handle');
    this.productFormElement = this.querySelector('form');
    this.submitButton = this.querySelector('[data-submit-product]');
    this.selectId = this.querySelector('[name="id"]');

    this.swatchColors = Array.from(this.querySelectorAll('.swatches__swatch.is-color'));
    this.colorNames = this.querySelector('.color-names');
    this.displayColorName = this.displayColorNames.bind(this);

    this.swatchColors.forEach(e => {
      e.addEventListener('mouseenter', this.displayColorName);
      e.addEventListener('mouseleave', this.displayColorName);
      e.addEventListener('click', this.displayColorName);
    });

    this.allSwatches = Array.from(this.querySelectorAll('[data-option-position]'));

    this.initializeForm();
    this.productFormElement.addEventListener('submit', this.submitProductForm.bind(this));
  }

  displayColorNames(event) {
    let colorName = event.currentTarget.querySelector('[data-swatch-colors]').innerHTML;

    if (event.type === 'mouseleave') {
      let allColorInputs = Array.from(this.querySelectorAll('input[name="options[Color||Couleur]"]'));

      for (const e of allColorInputs) {
        if (e.checked) {
          colorName = e.previousElementSibling.innerHTML; // '[data-swatch-colors]' innerHTML of checked input
          break;
        }
      }
    }

    this.colorNames.innerHTML = colorName;
  }

  async initializeForm() {
    this.product = await this.getProductJson(this.productHandle);

    this.productForm = new ProductForm(this.productFormElement, this.product, {
      onOptionChange: this.optionsChange.bind(this),
    });
  }

  getProductJson(handle) {
    return fetch(`/products/${handle}.js`).then((response) => {
      return response.json();
    });
  }

  async submitProductForm(event) {
    event.preventDefault();

    this.submitButton.classList.add('loading');

    await addToCartForm(this.productFormElement);

    this.submitButton.classList.remove('loading');
  }

  optionsChange(event) {
    const variant = event.dataset.variant;

    this.updateBrowserHistory(variant);
    this.updateMainSelect(variant);
    this.updateProductPrice(variant);
    this.updateOutOfStockOptions(variant);
    this.updateProductButton(variant);
    this.updateSlideImages(variant);
  }

  updateBrowserHistory(variant) {
    if (!variant) {
      return;
    }

    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({path: url}, '', url);
  }

  updateMainSelect(variant) {
    if(variant) {
      this.selectId.value = variant.id;
      this.selectId.dispatchEvent(new Event('change'));
    }
  }

  updateProductPrice(variant) {
    this.priceWrapperElement.classList.toggle('hide', !variant);

    if (variant) {
      this.priceElement.forEach(price => price.innerHTML = formatMoney(variant.price, theme.moneyFormat)?.replace('.00', ''));
      this.comparePriceElement.forEach(compareAtPrice => compareAtPrice.innerHTML = formatMoney(variant.compare_at_price, theme.moneyFormat)?.replace('.00', ''));

      if (variant.compare_at_price > variant.price) {
        this.priceWrapperElement.classList.add('price--on-sale');
      }
      else {
        this.priceWrapperElement.classList.remove('price--on-sale');
      }

      if (variant.available) {
        this.priceWrapperElement.classList.remove('price--sold-out');
      }
      else {
        this.priceWrapperElement.classList.add('price--sold-out');
      }
    }
  }

  updateProductButton(variant) {
    this.submitButton.innerHTML = "";

    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.unavailable;
    } else if (variant.available) {
      this.submitButton.disabled = false;
      this.submitButton.innerHTML = theme.strings.product.addToCart;
    } else {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.soldOut;
    }
  }

  updateOutOfStockOptions(variant) {
    const $option1 = this.querySelector('input[id^=Option1]:checked');
    const $option2 = this.querySelector('input[id^=Option2]:checked');

    const option1 = $option1 ? $option1.value : null;
    const option2 = $option2 ? $option1.value : null;

    this.allSwatches.forEach(optionInput => {
      if (optionInput.dataset.optionPosition == 1) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === optionInput.value &&
            variantCompare.available
        );
      }
      else if (optionInput.dataset.optionPosition == 2) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === optionInput.value &&
            variantCompare.available
        );
      }
      else if (optionInput.dataset.optionPosition == 3) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === option2 &&
            variantCompare.option3 === optionInput.value &&
            variantCompare.available
        );
      }

      
      if(availableWithValue) {
        optionInput.classList.remove('swatches__disabled');
      }
      else {
        optionInput.classList.add('swatches__disabled');
      }
    });
  }

  updateSlideImages(variant) {
    if(variant) {
      const colorOption = this.product.options.find(option => option.name.toLowerCase().includes('color') || option.name.toLowerCase().includes('couleur'));

      /**
       * if the product has a color
       * we display only the images that
       * has the color as the alt or no alt at all
       */
      if(colorOption) {
        const currentColor = variant.options[colorOption.position - 1];

        this.product.media.forEach(media => {
          const showImage = media.alt === currentColor || !media.alt;

          const imageElement = this.productImagesSwiper.parentElement.querySelectorAll(`[data-media-id="${media.id}"]`);

          if(imageElement) {
            imageElement.forEach(image => image.classList.toggle('hide', !showImage));
          }
        });

        if(this.productImagesSwiper.swiper) {
          /**
           * wihout triggering "resize" swiper gets broken
           * when you hide and show slides
           */
          window.dispatchEvent(new Event('resize'));
        }
      }

      if(this.productImagesSwiper.swiper) {
        /**
         * go to variant image or first image
         */
        if(variant.featured_media) {
          const imageId = variant.featured_media.id;
          const imageElement = this.productImagesSwiper.querySelector(`[data-media-id="${imageId}"]`);
          let imageIndex = [...this.productImagesSlides].indexOf(imageElement);

          if(imageIndex === -1) {
            imageIndex = 0;
          }

          this.productImagesSwiper.swiper.slideTo(imageIndex, 0);
        }
      }
      /**
       * if swiper is not initialized we're in scroll mode
       */
      else {
        /**
         * go to variant image or first image
         */
         if(variant.featured_media) {
          const imageId = variant.featured_media.id;
          const imageToScrollTo = this.productImagesSwiper.querySelector(`[data-media-id="${imageId}"]`) || this.productImagesSwiper;

          this.productImagesContainer.checkImageToSelect();

          window.scrollTo({
            top: imageToScrollTo.getBoundingClientRect().top + window.pageYOffset - 80,
            behavior: 'smooth'
          });
        }
      }
    }


  }
}

try {
  customElements.define('product-form', ProductFormElement);
}
catch(e) {
  console.log("product-form already defined", e)
}
